/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Chip, styled } from '@mui/material';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';
import { FormikValues } from 'formik';

import { ApplicationState } from 'store/store';
import { IPOSLocation } from 'components/menu/MenuPosApi';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { MuiCardForm } from 'lib/form/MuiCardForm';
import { SettingDisplay } from 'lib/SettingDisplay';
import { Option } from 'lib/types';
import { AlohaSettingsDisplay } from './PointOfSale/Aloha/AlohaSettingsDisplay';
import { AlohaSettingsEdit } from './PointOfSale/Aloha/AlohaSettingsEdit';
import { ComtrexSettingsDisplay } from './PointOfSale/Comtrex/ComtrexSettingsDisplay';
import { ComtrexSettingsEdit } from './PointOfSale/Comtrex/ComtrexSettingsEdit';
import { IBSSettingsDisplay } from './PointOfSale/IBS/IBSSettingsDisplay';
import { IBSSettingsEdit } from './PointOfSale/IBS/IBSSettingsEdit';
import { LightspeedSettingsDisplay } from './PointOfSale/Lightspeed/LightspeedSettingsDisplay';
import { LightspeedSettingsEdit } from './PointOfSale/Lightspeed/LightspeedSettingsEdit';
import { MicrosSettingsDisplay } from './PointOfSale/Micros/MicrosSettingsDisplay';
import { MicrosSettingsEdit } from './PointOfSale/Micros/MicrosSettingsEdit';
import { RevelSettingsDisplay } from './PointOfSale/Revel/RevelSettingsDisplay';
import { RevelSettingsEdit } from './PointOfSale/Revel/RevelSettingsEdit';
import { SquareSettingsDisplay } from './PointOfSale/Square/SquareSettingsDisplay';
import { SquareSettingsEdit } from './PointOfSale/Square/SquareSettingsEdit';
import { ZonalSettingsDisplay } from './PointOfSale/Zonal/ZonalSettingsDisplay';
import { ZonalSettingsEdit } from './PointOfSale/Zonal/ZonalSettingsEdit';
import { ILocationScopedSettingsComponentProps } from './LocationScopedSettings';
import { SettingDisplaySwitch } from './SettingDisplaySwitch';
import { DeliverectSettingsDisplay } from './PointOfSale/Deliverect/DeliverectSettingsDisplay';
import { DeliverectSettingsEdit } from './PointOfSale/Deliverect/DeliverectSettingsEdit';
import { GoodtillSettingsDisplay } from './PointOfSale/Goodtill/GoodtillSettingsDisplay';
import { GoodtillSettingsEdit } from './PointOfSale/Goodtill/GoodtillSettingsEdit';
import { SettingDisplayText } from './SettingDisplayText';
import { TevalisSettingsDisplay } from './PointOfSale/Tevalis/TevalisSettingsDisplay';
import { TevalisSettingsEdit } from './PointOfSale/Tevalis/TevalisSettingsEdit';
import { McrSettingsDisplay } from './PointOfSale/Mcr/McrSettingsDisplay';
import { McrSettingsEdit } from './PointOfSale/Mcr/McrSettingsEdit';
import { SimphonySettingsDisplay } from './PointOfSale/Simphony/SimphonySettingsDisplay';
import { SimphonySettingsEdit } from './PointOfSale/Simphony/SimphonySettingsEdit';
import { usePointOfSaleSettings } from './PointOfSale/usePointOfSaleSettings';
import { CreatableChipInputFormField } from 'lib/form/fields/CreatableChipInputFormField';
import { QikserveSettingsDisplay } from './PointOfSale/Qikserve/QikserveSettingsDisplay';
import { QikserveSettingsEdit } from './PointOfSale/Qikserve/QikserveSettingsEdit';

const PREFIX = 'PointOfSaleSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(1)
    }
}));

export interface IPointOfSaleSettingsProps {
    locationId?: string;
    settings?: IPublicAndPrivateSettings;
    globalSettings?: IPublicAndPrivateSettings;
    onSettingsSaved?: ILocationScopedSettingsComponentProps['onSettingsSaved'];
    posLocations?: IPOSLocation[];
    hasInternalSettingsWritePermission?: boolean;
    hasInternalSettingsReadPermission?: boolean;
}

export enum EPointOfSaleProvider {
    REVEL = 'REVEL',
    PEPPER = 'PEPPER',
    IBS = 'IBS',
    LAVU = 'LAVU',
    POINTONE = 'POINTONE',
    SQUARE = 'SQUARE',
    LIGHTSPEED = 'LIGHTSPEED',
    NOBLY = 'NOBLY',
    OMNIVORE_MICROS_SIMPHONY = 'OMNIVORE_MICROS_SIMPHONY',
    OMNIVORE_MICROS_3700 = 'OMNIVORE_MICROS_3700',
    OMNIVORE_NCR_ALOHA = 'OMNIVORE_NCR_ALOHA',
    COMTREX = 'COMTREX',
    ZONAL_AZTEC = 'ZONAL_AZTEC',
    DELIVERECT = 'DELIVERECT',
    GOODTILL = 'GOODTILL',
    TEVALIS = 'TEVALIS',
    MCR = 'MCR',
    SIMPHONY = 'SIMPHONY',
    QIKSERVE = 'QIKSERVE_CONNECT_3SPOS',
    NONE = 'NONE'
}

const posProviderOptions: Option[] = [
    { label: 'Omnivore Micros Simphony', value: EPointOfSaleProvider.OMNIVORE_MICROS_SIMPHONY },
    { label: 'Omnivore Micros 3700', value: EPointOfSaleProvider.OMNIVORE_MICROS_3700 },
    { label: 'Omnivore NCR Aloha', value: EPointOfSaleProvider.OMNIVORE_NCR_ALOHA },
    { label: 'IBS Access', value: EPointOfSaleProvider.IBS },
    { label: 'Revel', value: EPointOfSaleProvider.REVEL },
    { label: 'Lightspeed', value: EPointOfSaleProvider.LIGHTSPEED },
    { label: 'Comtrex', value: EPointOfSaleProvider.COMTREX },
    { label: 'Zonal Aztec', value: EPointOfSaleProvider.ZONAL_AZTEC },
    { label: 'Square', value: EPointOfSaleProvider.SQUARE },
    { label: 'Deliverect', value: EPointOfSaleProvider.DELIVERECT },
    { label: 'Goodtill', value: EPointOfSaleProvider.GOODTILL },
    { label: 'Tevalis', value: EPointOfSaleProvider.TEVALIS },
    { label: 'MCR', value: EPointOfSaleProvider.MCR },
    { label: 'Simphony', value: EPointOfSaleProvider.SIMPHONY },
    { label: 'Qikserve', value: EPointOfSaleProvider.QIKSERVE }
];

enum ERefreshSchedule {
    EVERY_2_HOURS = 'Every2Hours',
    DAILY_4_AM = 'Daily4AM',
    DAILY_7_AM = 'Daily7AM'
}

const refreshScheduleLabels: Record<string, string> = {
    [ERefreshSchedule.DAILY_4_AM]: 'Daily at 4 am',
    [ERefreshSchedule.DAILY_7_AM]: 'Daily at 7 am',
    [ERefreshSchedule.EVERY_2_HOURS]: 'Every 2 hours'
};

const refreshScheduleOptions = [
    { label: 'Default', value: null },
    {
        value: ERefreshSchedule.DAILY_4_AM,
        label: refreshScheduleLabels[ERefreshSchedule.DAILY_4_AM]
    },
    {
        value: ERefreshSchedule.DAILY_7_AM,
        label: refreshScheduleLabels[ERefreshSchedule.DAILY_7_AM]
    },
    {
        value: ERefreshSchedule.EVERY_2_HOURS,
        label: refreshScheduleLabels[ERefreshSchedule.EVERY_2_HOURS]
    }
];

export enum EItemMappingScheme {
    MAP_FIRST_MATCHED_PRICELEVEL = 'MAP_FIRST_MATCHED_PRICELEVEL',
    MAP_MATCHED_PRICELEVELS_AS_VARIANTS = 'MAP_MATCHED_PRICELEVELS_AS_VARIANTS',
    MAP_FIRST_MATCHED_PRICELEVEL_ID = 'MAP_FIRST_MATCHED_PRICELEVEL_ID'
}

export const itemMappingSchemeLabel: Record<string, string> = {
    [EItemMappingScheme.MAP_FIRST_MATCHED_PRICELEVEL]: 'Map Matched Price Level',
    [EItemMappingScheme.MAP_MATCHED_PRICELEVELS_AS_VARIANTS]: 'Map Matched Price Level As Variants',
    [EItemMappingScheme.MAP_FIRST_MATCHED_PRICELEVEL_ID]: 'Map First Matched Price Level ID'
};

export const itemMappingSchemeOptions: Option[] = [
    { label: 'None', value: null },
    {
        label: itemMappingSchemeLabel[EItemMappingScheme.MAP_FIRST_MATCHED_PRICELEVEL],
        value: EItemMappingScheme.MAP_FIRST_MATCHED_PRICELEVEL
    },
    {
        label: itemMappingSchemeLabel[EItemMappingScheme.MAP_MATCHED_PRICELEVELS_AS_VARIANTS],
        value: EItemMappingScheme.MAP_MATCHED_PRICELEVELS_AS_VARIANTS
    },
    {
        label: itemMappingSchemeLabel[EItemMappingScheme.MAP_FIRST_MATCHED_PRICELEVEL_ID],
        value: EItemMappingScheme.MAP_FIRST_MATCHED_PRICELEVEL_ID
    }
];

interface IPosSettingsComponentConfig {
    display: React.FC<IPointOfSaleSettingsProps> | null;
    edit: React.FC<IPointOfSaleSettingsProps> | null;
}
const posSettingsComponentConfig: Record<EPointOfSaleProvider, IPosSettingsComponentConfig> = {
    [EPointOfSaleProvider.OMNIVORE_MICROS_SIMPHONY]: {
        display: MicrosSettingsDisplay,
        edit: MicrosSettingsEdit
    },
    [EPointOfSaleProvider.OMNIVORE_MICROS_3700]: { display: MicrosSettingsDisplay, edit: MicrosSettingsEdit },
    [EPointOfSaleProvider.OMNIVORE_NCR_ALOHA]: { display: AlohaSettingsDisplay, edit: AlohaSettingsEdit },
    [EPointOfSaleProvider.IBS]: { display: IBSSettingsDisplay, edit: IBSSettingsEdit },
    [EPointOfSaleProvider.REVEL]: { display: RevelSettingsDisplay, edit: RevelSettingsEdit },
    [EPointOfSaleProvider.LIGHTSPEED]: { display: LightspeedSettingsDisplay, edit: LightspeedSettingsEdit },
    [EPointOfSaleProvider.COMTREX]: { display: ComtrexSettingsDisplay, edit: ComtrexSettingsEdit },
    [EPointOfSaleProvider.ZONAL_AZTEC]: { display: ZonalSettingsDisplay, edit: ZonalSettingsEdit },
    [EPointOfSaleProvider.SQUARE]: { display: SquareSettingsDisplay, edit: SquareSettingsEdit },
    [EPointOfSaleProvider.DELIVERECT]: { display: DeliverectSettingsDisplay, edit: DeliverectSettingsEdit },
    [EPointOfSaleProvider.GOODTILL]: { display: GoodtillSettingsDisplay, edit: GoodtillSettingsEdit },
    [EPointOfSaleProvider.TEVALIS]: { display: TevalisSettingsDisplay, edit: TevalisSettingsEdit },
    [EPointOfSaleProvider.PEPPER]: { display: null, edit: null },
    [EPointOfSaleProvider.NOBLY]: { display: null, edit: null },
    [EPointOfSaleProvider.LAVU]: { display: null, edit: null },
    [EPointOfSaleProvider.POINTONE]: { display: null, edit: null },
    [EPointOfSaleProvider.MCR]: { display: McrSettingsDisplay, edit: McrSettingsEdit },
    [EPointOfSaleProvider.SIMPHONY]: { display: SimphonySettingsDisplay, edit: SimphonySettingsEdit },
    [EPointOfSaleProvider.QIKSERVE]: { display: QikserveSettingsDisplay, edit: QikserveSettingsEdit },
    [EPointOfSaleProvider.NONE]: { display: null, edit: null }
};

export const PointOfSaleSettings: React.FC<ILocationScopedSettingsComponentProps> = ({
    settings,
    onSettingsSaved,
    locationId,
    globalSettings
}) => {
    const { initialValues, posProvider, handleSubmitPosProvider } = usePointOfSaleSettings({
        settings,
        globalSettings,
        locationId,
        onSettingsSaved
    });

    const { posLocations } = useSelector((state: ApplicationState) => state.posLocations);

    const showAutoRefreshProductAvailability = React.useMemo(
        () =>
            // not SQUARE, since it uses webhooks
            posProvider === EPointOfSaleProvider.DELIVERECT ||
            posProvider === EPointOfSaleProvider.OMNIVORE_NCR_ALOHA ||
            posProvider === EPointOfSaleProvider.REVEL ||
            posProvider === EPointOfSaleProvider.ZONAL_AZTEC ||
            posProvider === EPointOfSaleProvider.OMNIVORE_MICROS_SIMPHONY ||
            posProvider === EPointOfSaleProvider.OMNIVORE_MICROS_3700,
        [posProvider]
    );

    const renderPosEdit = React.useCallback(
        (values: FormikValues) => {
            const PosEditComponent =
                posSettingsComponentConfig[values.posProvider as EPointOfSaleProvider]?.edit;
            const locationPosProviderOptions = posProviderOptions.filter(
                ({ value }) =>
                    value === values.posProvider ||
                    value === globalSettings?.posProvider ||
                    value === EPointOfSaleProvider.DELIVERECT
            );
            return (
                <Box display="flex" flexDirection="column">
                    {locationId !== undefined && (
                        <>
                            <SettingDisplay label="POS Provider">
                                <SelectFormField options={locationPosProviderOptions} name="posProvider" />
                            </SettingDisplay>
                            {posProvider !== EPointOfSaleProvider.SQUARE && (
                                <SettingDisplay label="Refresh Schedule">
                                    <SelectFormField
                                        name="refreshSchedule"
                                        options={refreshScheduleOptions}
                                        emptyLabel="Default"
                                        displayEmpty
                                    />
                                </SettingDisplay>
                            )}
                        </>
                    )}
                    {locationId === undefined && (
                        <>
                            <SettingDisplay label="POS Provider">
                                <SelectFormField options={posProviderOptions} name="posProvider" />
                            </SettingDisplay>
                            <SettingDisplay label="Auto Refresh">
                                <SwitchFormField name="autoRefreshEnabled" />
                            </SettingDisplay>
                            <SettingDisplay label="Manual Menu Refresh">
                                <SwitchFormField name="manualRefreshEnabled" />
                            </SettingDisplay>
                            {showAutoRefreshProductAvailability && (
                                <SettingDisplay label="Auto Refresh Product Availability ">
                                    <SwitchFormField name="autoRefreshAvailabilityEnabled" />
                                </SettingDisplay>
                            )}
                            <SettingDisplay label="Hide Item Ids on Pepper Email Receipt">
                                <CreatableChipInputFormField
                                    name="hiddenProductIds"
                                    placeholder=""
                                    limitTags={4}
                                    maxInputHeight={100}
                                />
                            </SettingDisplay>
                            {posProvider !== EPointOfSaleProvider.SQUARE && (
                                <SettingDisplay label="Refresh Schedule">
                                    <SelectFormField
                                        name="refreshSchedule"
                                        options={refreshScheduleOptions}
                                        emptyLabel="Default"
                                        displayEmpty
                                    />
                                </SettingDisplay>
                            )}
                        </>
                    )}
                    {PosEditComponent && (
                        <PosEditComponent
                            settings={settings}
                            globalSettings={globalSettings}
                            locationId={locationId}
                            posLocations={posLocations}
                            onSettingsSaved={onSettingsSaved}
                        />
                    )}
                </Box>
            );
        },
        [
            locationId,
            showAutoRefreshProductAvailability,
            settings,
            globalSettings,
            posLocations,
            onSettingsSaved,
            posProvider
        ]
    );

    const PosDisplayComponent = React.useMemo(
        () => posSettingsComponentConfig[posProvider as EPointOfSaleProvider]?.display,
        [posProvider]
    );

    return (
        <Box minWidth="500px">
            {/* Cards for each settings block */}
            <StyledMuiCardForm
                title="Point of Sale"
                initialValues={initialValues}
                onSubmit={handleSubmitPosProvider}
                childrenWhileDisplaying={
                    <Box display="flex" flexDirection="column">
                        <SettingDisplay label="POS Provider">
                            <Chip
                                label={
                                    posProviderOptions.find(option => option.value === posProvider)?.label ??
                                    'None'
                                }
                                color="primary"
                            />
                        </SettingDisplay>
                        {locationId === undefined && (
                            <>
                                <SettingDisplaySwitch
                                    checked={settings?.menu?.autoRefreshEnabled ?? true}
                                    title="Auto Menu Refresh"
                                />
                                <SettingDisplaySwitch
                                    checked={settings?.menu?.manualRefreshEnabled ?? true}
                                    title="Manual Menu Refresh"
                                />
                                {showAutoRefreshProductAvailability && (
                                    <SettingDisplaySwitch
                                        checked={settings?.menu?.autoRefreshAvailabilityEnabled ?? true}
                                        title="Auto Refresh Product Availability"
                                    />
                                )}
                                <SettingDisplayText
                                    text={
                                        refreshScheduleLabels[settings?.menu?.refreshSchedule] ??
                                        'Default (Every 2 hours)'
                                    }
                                    title="Refresh Schedule"
                                />
                                <SettingDisplayText
                                    text={settings?.orderPosSync?.hiddenProductIds?.join(',') || ''}
                                    title="Hide Item Ids on Pepper Email Receipt"
                                />
                            </>
                        )}
                        {locationId !== undefined && posProvider !== EPointOfSaleProvider.SQUARE && (
                            <SettingDisplayText
                                text={refreshScheduleLabels[settings?.menu?.refreshSchedule] ?? ''}
                                title="Refresh Schedule"
                            />
                        )}
                        {PosDisplayComponent && (
                            <PosDisplayComponent
                                settings={settings}
                                globalSettings={globalSettings}
                                locationId={locationId}
                                posLocations={posLocations}
                                onSettingsSaved={onSettingsSaved}
                            />
                        )}
                    </Box>
                }
                childrenWhileEditing={renderPosEdit}
                className={classes.settingsCard}
                boxClassName={classes.displayClassName}
            />
        </Box>
    );
};
